import React, { createContext, useContext, useState, useEffect } from "react";

/**
 * Context for managing cookie consent and preferences
 * Handles GDPR compliance with 13-month consent expiration
 */
const CookieContext = createContext();

export const useCookie = () => {
  const context = useContext(CookieContext);
  if (!context) {
    throw new Error("useCookie must be used within a CookieProvider");
  }
  return context;
};

export const CookieProvider = ({ children }) => {
  const [cookieConsent, setCookieConsent] = useState(null); // null = not set, true = accepted, false = rejected
  const [cookiePreferences, setCookiePreferences] = useState({
    necessary: true, // Always true, cannot be disabled
    analytics: false,
    marketing: false,
  });
  const [bannerVisible, setBannerVisible] = useState(false);

  // Cookie consent status
  const CONSENT_STATUS = {
    NOT_SET: null,
    ACCEPTED: "accepted",
    REJECTED: "rejected",
    CUSTOM: "custom",
  };

  // Check if consent is expired
  const isConsentExpired = () => {
    const consentDate = localStorage.getItem("cookie-consent-date");
    if (!consentDate) return true;

    const consent = new Date(consentDate);
    const now = new Date();
    const thirteenMonths = 13 * 30 * 24 * 60 * 60 * 1000; // 13 months in milliseconds

    return now - consent > thirteenMonths;
  };

  // Load cookie preferences from localStorage on mount
  useEffect(() => {
    const storedConsent = localStorage.getItem("cookie-consent");
    const storedPreferences = localStorage.getItem("cookie-preferences");

    // Check if consent is expired
    const consentExpired = isConsentExpired();

    if (storedConsent && !consentExpired) {
      setCookieConsent(storedConsent);
    } else if (consentExpired) {
      // Clear expired consent
      localStorage.removeItem("cookie-consent");
      localStorage.removeItem("cookie-preferences");
      localStorage.removeItem("cookie-consent-date");
    }

    if (storedPreferences && !consentExpired) {
      try {
        const preferences = JSON.parse(storedPreferences);
        setCookiePreferences({
          necessary: true, // Always enforce necessary cookies
          analytics: preferences.analytics || false,
          marketing: preferences.marketing || false,
        });
      } catch (error) {
        console.warn("Invalid cookie preferences in localStorage:", error);
      }
    }

    // Show banner if no consent has been given or consent expired
    // Enabled by default, can be disabled with REACT_APP_ENABLE_COOKIES=false
    const enableCookies = process.env.REACT_APP_ENABLE_COOKIES !== "false";

    setBannerVisible(enableCookies && (!storedConsent || consentExpired));
  }, []);

  // Save preferences to localStorage
  const savePreferences = (consent, preferences) => {
    localStorage.setItem("cookie-consent", consent);
    localStorage.setItem("cookie-preferences", JSON.stringify(preferences));
    localStorage.setItem("cookie-consent-date", new Date().toISOString());
  };

  // Accept all cookies
  const acceptAllCookies = () => {
    const newPreferences = {
      necessary: true,
      analytics: true,
      marketing: true,
    };

    setCookieConsent(CONSENT_STATUS.ACCEPTED);
    setCookiePreferences(newPreferences);
    setBannerVisible(false);

    savePreferences(CONSENT_STATUS.ACCEPTED, newPreferences);
  };

  // Reject optional cookies (keep only necessary)
  const rejectOptionalCookies = () => {
    const newPreferences = {
      necessary: true,
      analytics: false,
      marketing: false,
    };

    setCookieConsent(CONSENT_STATUS.REJECTED);
    setCookiePreferences(newPreferences);
    setBannerVisible(false);

    savePreferences(CONSENT_STATUS.REJECTED, newPreferences);
  };

  // Set custom preferences
  const setCustomPreferences = (preferences) => {
    const newPreferences = {
      necessary: true, // Always enforce necessary cookies
      analytics: preferences.analytics || false,
      marketing: preferences.marketing || false,
    };

    setCookieConsent(CONSENT_STATUS.CUSTOM);
    setCookiePreferences(newPreferences);
    setBannerVisible(false);

    savePreferences(CONSENT_STATUS.CUSTOM, newPreferences);
  };

  // Reset cookie consent, used for testing or user request
  const resetConsent = () => {
    setCookieConsent(null);
    setCookiePreferences({
      necessary: true,
      analytics: false,
      marketing: false,
    });
    setBannerVisible(process.env.REACT_APP_ENABLE_COOKIES !== "false");

    localStorage.removeItem("cookie-consent");
    localStorage.removeItem("cookie-preferences");
    localStorage.removeItem("cookie-consent-date");
  };

  // Reopen banner to allow users to change their cookie preferences
  const reopenBanner = () => {
    setBannerVisible(true);
  };

  // Check if specific cookie type is allowed
  const isCookieAllowed = (cookieType) => {
    return cookiePreferences[cookieType] || false;
  };

  // Get consent information
  const getConsentInfo = () => {
    const consentDate = localStorage.getItem("cookie-consent-date");
    return {
      status: cookieConsent,
      preferences: cookiePreferences,
      date: consentDate ? new Date(consentDate) : null,
      hasConsent: cookieConsent !== null,
    };
  };

  const value = {
    // State
    cookieConsent,
    cookiePreferences,
    bannerVisible,

    // Constants
    CONSENT_STATUS,

    // Actions
    acceptAllCookies,
    rejectOptionalCookies,
    setCustomPreferences,
    resetConsent,
    reopenBanner,

    // Utilities
    isCookieAllowed,
    getConsentInfo,
  };

  return <CookieContext.Provider value={value}>{children}</CookieContext.Provider>;
};
